import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Package, Plus, Search, Edit2, Trash2, X, AlertTriangle, ArrowDownToLine,
  ArrowUpFromLine, LayoutDashboard, Boxes, FileText, History, ChevronDown,
  CheckCircle2, AlertCircle, TrendingDown, Loader, BarChart3, UserPlus,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authHeaders } from '../utils/api';
import { fetchJsonResilient } from '../utils/resilientFetch';
import SupplierForm, { formatCnpjDisplay } from '../components/SupplierForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const formatBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatQty = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

const NATUREZA_COLORS = {
  n1_os_ativo: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  n2_setor: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  n3_consumo_geral: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
};

// Rótulo identificável de um ativo no seletor de Saída (Natureza 1).
// Inclui um identificador (placa/nº de série) e o Centro de Custo que será
// derivado automaticamente — evitando seleção ambígua entre máquinas de mesmo nome.
// ============ Hook utilitário ============
function useCatalog() {
  const [unidades, setUnidades] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [naturezas, setNaturezas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [centros, setCentros] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const h = { headers: authHeaders() };
        // Carregamento resiliente: evita dropdowns vazios por blip transitório do
        // Service Worker (503) ou desafio Cloudflare ao abrir a tela.
        const [u, c, n, f, cc, wo, at, mq] = await Promise.all([
          fetchJsonResilient(`${API_URL}/api/almoxarifado/unidades`, h),
          fetchJsonResilient(`${API_URL}/api/almoxarifado/categorias`, h),
          fetchJsonResilient(`${API_URL}/api/almoxarifado/naturezas-saida`, h),
          fetchJsonResilient(`${API_URL}/api/fornecedores`, h),
          fetchJsonResilient(`${API_URL}/api/centros-custo?status=ativo`, h),
          fetchJsonResilient(`${API_URL}/api/work-orders?status_filter=open`, h),
          fetchJsonResilient(`${API_URL}/api/inventario/ativos`, h),
          fetchJsonResilient(`${API_URL}/api/machines`, h),
        ]);
        if (u) setUnidades(u);
        if (c) setCategorias(c);
        if (n) setNaturezas(n);
        if (f) setFornecedores(f);
        if (cc) setCentros(cc);
        if (Array.isArray(wo)) setWorkOrders(wo);
        if (Array.isArray(at)) setAtivos(at);
        if (Array.isArray(mq)) setMachines(mq);
      } catch (e) { console.error('catalog load:', e); }
    };
    load();
  }, []);

  return { unidades, categorias, naturezas, fornecedores, centros, workOrders, ativos, machines };
}

// ============ Componente: Itens ============
function ItensTab({ catalog, canEdit, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [filterConsumo, setFilterConsumo] = useState('all');
  const [showOnlyBelow, setShowOnlyBelow] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);

  const emptyForm = {
    nome: '', codigo: '', unidade_medida: 'un', categoria: 'consumivel',
    estoque_minimo: 0, consumo_geral: false, descricao: '', ativo: true,
    marca: '', nf_numero: '', nf_serie: '', nf_chave_acesso: '', nf_data_emissao: '',
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategoria !== 'all') params.append('categoria', filterCategoria);
      if (filterConsumo !== 'all') params.append('consumo_geral', filterConsumo === 'yes' ? 'true' : 'false');
      if (showOnlyBelow) params.append('apenas_abaixo_minimo', 'true');
      const r = await fetch(`${API_URL}/api/almoxarifado/itens?${params}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Falha ao carregar itens');
      setItems(await r.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [search, filterCategoria, filterConsumo, showOnlyBelow]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nome: it.nome, codigo: it.codigo || '', unidade_medida: it.unidade_medida,
      categoria: it.categoria, estoque_minimo: it.estoque_minimo,
      consumo_geral: it.consumo_geral, descricao: it.descricao || '', ativo: it.ativo,
      marca: it.marca || '', nf_numero: it.nf_numero || '', nf_serie: it.nf_serie || '',
      nf_chave_acesso: it.nf_chave_acesso || '', nf_data_emissao: it.nf_data_emissao || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.nome || form.nome.trim().length < 2) return toast.error('Nome obrigatório (mín. 2 chars)');
    setSaving(true);
    try {
      const url = editing
        ? `${API_URL}/api/almoxarifado/itens/${editing.id}`
        : `${API_URL}/api/almoxarifado/itens`;
      const method = editing ? 'PUT' : 'POST';
      const body = { ...form, estoque_minimo: Number(form.estoque_minimo) || 0 };
      const r = await fetch(url, {
        method, headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro ao salvar'); }
      toast.success(editing ? 'Item atualizado' : 'Item criado');
      setShowModal(false);
      fetchItems();
      onChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (it) => {
    try {
      const r = await fetch(`${API_URL}/api/almoxarifado/itens/${it.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.status === 409) {
        const d = await r.json();
        toast.warning(d.detail || 'Item foi inativado por possuir movimentações');
      } else if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Erro ao excluir');
      } else {
        toast.success('Item excluído');
      }
      setConfirmDelete(null);
      fetchItems();
      onChange?.();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="almox-itens-tab">
      {/* Header de ações */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            data-testid="almox-itens-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou código..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none"
          />
        </div>
        <select
          data-testid="almox-itens-filter-categoria"
          value={filterCategoria} onChange={(e) => setFilterCategoria(e.target.value)}
          className="px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white"
        >
          <option value="all">Todas categorias</option>
          {catalog.categorias.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select
          value={filterConsumo} onChange={(e) => setFilterConsumo(e.target.value)}
          className="px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white"
        >
          <option value="all">Todos itens</option>
          <option value="yes">Só consumo geral</option>
          <option value="no">Sem consumo geral</option>
        </select>
        <label className="flex items-center gap-2 px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white text-sm cursor-pointer">
          <input
            data-testid="almox-itens-toggle-abaixo"
            type="checkbox" checked={showOnlyBelow} onChange={(e) => setShowOnlyBelow(e.target.checked)}
            className="accent-yellow-500"
          />
          Abaixo do mínimo
        </label>
        {canEdit && (
          <button
            data-testid="almox-itens-novo"
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
          >
            <Plus size={18} /> Novo Item
          </button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader className="animate-spin text-yellow-500" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Package size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhum item encontrado.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="almox-itens-lista">
          {items.map(it => (
            <div key={it.id} className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors"
                 data-testid={`almox-item-${it.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold">{it.nome}</h3>
                    {it.codigo && <span className="text-xs text-gray-500">#{it.codigo}</span>}
                    {it.consumo_geral && (
                      <span className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded">
                        Consumo Geral
                      </span>
                    )}
                    {!it.ativo && (
                      <span className="text-xs px-2 py-0.5 bg-gray-500/20 text-gray-400 border border-gray-500/40 rounded">Inativo</span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">{it.categoria_label} • {it.unidade_medida_label}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${it.abaixo_minimo ? 'text-red-400' : 'text-white'}`}>
                    {formatQty(it.saldo_atual)}
                  </p>
                  <p className="text-xs text-gray-500">mín. {formatQty(it.estoque_minimo)}</p>
                  {it.abaixo_minimo && (
                    <span className="inline-flex items-center gap-1 text-xs text-red-400 mt-1">
                      <AlertTriangle size={12} /> abaixo do mínimo
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setHistoryOf(it)}
                          data-testid={`almox-item-historico-${it.id}`}
                          className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300" title="Histórico">
                    <History size={16} />
                  </button>
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(it)}
                              data-testid={`almox-item-edit-${it.id}`}
                              className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300" title="Editar">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => setConfirmDelete(it)}
                              data-testid={`almox-item-del-${it.id}`}
                              className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300" title="Excluir">
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal create/edit */}
      {showModal && form && (
        <Modal title={editing ? 'Editar Item' : 'Novo Item'} onClose={() => setShowModal(false)} testid="almox-item-modal">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400">Nome *</label>
              <input data-testid="almox-item-input-nome"
                     value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Código (opcional)</label>
                <input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Estoque mínimo</label>
                <input data-testid="almox-item-input-minimo"
                       type="number" min="0" step="any"
                       value={form.estoque_minimo} onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Unidade *</label>
                <select value={form.unidade_medida} onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  {catalog.unidades.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400">Categoria *</label>
                <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  {catalog.categorias.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-start gap-2 p-3 bg-emerald-500/5 border border-emerald-500/30 rounded-lg cursor-pointer">
              <input data-testid="almox-item-checkbox-consumo-geral"
                     type="checkbox" checked={form.consumo_geral}
                     onChange={(e) => setForm({ ...form, consumo_geral: e.target.checked })}
                     className="accent-emerald-400 mt-0.5" />
              <span className="text-sm text-emerald-200">
                <strong>Consumo Geral</strong> — item de baixo valor que pode sair sem vinculação a Centro de Custo (Natureza 3).
              </span>
            </label>
            <div>
              <label className="text-xs text-gray-400">Descrição</label>
              <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>

            <FiscalSection form={form} setForm={setForm} />
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg">Cancelar</button>
            <button data-testid="almox-item-save" disabled={saving} onClick={handleSave}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg disabled:opacity-50">
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal title="Confirmar exclusão" onClose={() => setConfirmDelete(null)}>
          <p className="text-gray-300">
            Excluir o item <strong>{confirmDelete.nome}</strong>? Se houver movimentações, ele será inativado.
          </p>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 bg-gray-800 text-white rounded-lg">Cancelar</button>
            <button onClick={() => handleDelete(confirmDelete)}
                    data-testid="almox-item-confirm-del"
                    className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg">Excluir</button>
          </div>
        </Modal>
      )}

      {historyOf && <HistoryModal item={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

// ============ Modal genérico ============
function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-[#111] border border-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-[#111]">
          <h3 className="text-white font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// ============ HistoryModal ============
function HistoryModal({ item, onClose }) {
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/almoxarifado/itens/${item.id}/movimentacoes`, { headers: authHeaders() });
        if (r.ok) setMovs(await r.json());
      } finally { setLoading(false); }
    })();
  }, [item.id]);
  return (
    <Modal title={`Movimentações — ${item.nome}`} onClose={onClose} testid="almox-history-modal">
      {loading ? <Loader className="animate-spin text-yellow-500 mx-auto" /> : (
        movs.length === 0 ? <p className="text-gray-500 text-sm">Sem movimentações.</p> :
        <div className="space-y-2">
          {movs.map((m, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-gray-800">
              <div className="flex items-center gap-2">
                {m.tipo === 'entrada' ? (
                  <ArrowDownToLine size={16} className="text-green-400" />
                ) : (
                  <ArrowUpFromLine size={16} className="text-red-400" />
                )}
                <div>
                  <p className="text-white text-sm">{m.descricao}</p>
                  <p className="text-gray-500 text-xs">{m.data}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={`font-semibold ${m.tipo === 'entrada' ? 'text-green-400' : 'text-red-400'}`}>
                  {m.tipo === 'entrada' ? '+' : '-'}{formatQty(m.quantidade)} {item.unidade_medida_label}
                </p>
                {m.saldo_apos !== null && m.saldo_apos !== undefined && (
                  <p className="text-xs text-gray-500">saldo: {formatQty(m.saldo_apos)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ============ EntradasTab ============
function EntradasTab({ catalog, canEdit, onChange }) {
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCC, setFilterCC] = useState('all');
  const [filterModal, setFilterModal] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [fornecedoresLocal, setFornecedoresLocal] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [editingEntrada, setEditingEntrada] = useState(null);
  const [showHistEntrada, setShowHistEntrada] = useState(null);

  // sync fornecedores quando catalog atualiza
  useEffect(() => { setFornecedoresLocal(catalog.fornecedores || []); }, [catalog.fornecedores]);

  const reloadFornecedores = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/fornecedores`, { headers: authHeaders() });
      if (r.ok) setFornecedoresLocal(await r.json());
    } catch (e) { /* noop */ }
  }, []);

  const fornecedorSelecionado = useMemo(() => {
    if (!form?.fornecedor_id) return null;
    return fornecedoresLocal.find(f => f.id === form.fornecedor_id) || null;
  }, [form, fornecedoresLocal]);

  const fornecedoresFiltrados = useMemo(() => {
    const q = (supplierSearch || '').toLowerCase().trim();
    const ativos = fornecedoresLocal.filter(f => f.ativo);
    if (!q) return ativos.slice(0, 30);
    return ativos.filter(f =>
      (f.nome || '').toLowerCase().includes(q) ||
      (f.cnpj || '').includes(q) ||
      (f.pessoa_contato || '').toLowerCase().includes(q),
    ).slice(0, 30);
  }, [supplierSearch, fornecedoresLocal]);

  const fetchEntradas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCC !== 'all') params.append('centro_de_custo_id', filterCC);
      if (filterModal !== 'all') params.append('modalidade_pagamento', filterModal);
      const r = await fetch(`${API_URL}/api/almoxarifado/entradas?${params}`, { headers: authHeaders() });
      if (r.ok) setEntradas(await r.json());
    } finally { setLoading(false); }
  }, [filterCC, filterModal]);

  useEffect(() => { fetchEntradas(); }, [fetchEntradas]);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/almoxarifado/itens?ativo=true`, { headers: authHeaders() });
      if (r.ok) setItems(await r.json());
    })();
  }, []);

  const openCreate = () => {
    setForm({
      data: todayISO(),
      fornecedor_id: '',
      fornecedor_nome: '',
      modalidade_pagamento: 'a_vista',
      forma_pagamento: 'pix',
      destino: 'geral', // 'geral' | 'cc'
      centro_de_custo_id: '',
      numero_nota: '',
      observacoes: '',
      itens: [{ item_id: '', quantidade: '', valor_unitario: '' }],
      // parcelamento (Bloco 7)
      numero_parcelas: 1,
      data_primeiro_vencimento: '',
    });
    setEditingEntrada(null);
    setShowModal(true);
  };

  const openEditEntrada = (e) => {
    setEditingEntrada(e);
    setForm({
      data: e.data,
      fornecedor_id: e.fornecedor_id || '',
      fornecedor_nome: e.fornecedor_nome || '',
      modalidade_pagamento: e.modalidade_pagamento,
      forma_pagamento: e.forma_pagamento || 'pix',
      destino: e.centro_de_custo_id ? 'cc' : 'geral',
      centro_de_custo_id: e.centro_de_custo_id || '',
      numero_nota: e.numero_nota || '',
      observacoes: e.observacoes || '',
      itens: e.itens.map(it => ({
        item_id: it.item_id, quantidade: it.quantidade,
        valor_unitario: it.valor_unitario ?? '',
      })),
      numero_parcelas: e.numero_parcelas || 1,
      data_primeiro_vencimento: e.data_primeiro_vencimento || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.data) return toast.error('Data obrigatória');
    const linhas = form.itens.filter(l => l.item_id && Number(l.quantidade) > 0);
    if (linhas.length === 0) return toast.error('Adicione ao menos um item válido');
    if (form.destino === 'cc' && !form.centro_de_custo_id) return toast.error('Selecione o centro de custo');
    setSaving(true);
    try {
      const body = {
        data: form.data,
        modalidade_pagamento: form.modalidade_pagamento,
        fornecedor_id: form.fornecedor_id || null,
        fornecedor_nome: form.fornecedor_nome || null,
        centro_de_custo_id: form.destino === 'cc' ? form.centro_de_custo_id : null,
        numero_nota: form.numero_nota || null,
        observacoes: form.observacoes || null,
        itens: linhas.map(l => ({
          item_id: l.item_id,
          quantidade: Number(l.quantidade),
          valor_unitario: l.valor_unitario === '' ? null : Number(l.valor_unitario),
        })),
      };
      // Parcelamento (Bloco 7) — só envia se a_prazo
      if (form.modalidade_pagamento === 'a_prazo') {
        body.numero_parcelas = Number(form.numero_parcelas) || 1;
        body.data_primeiro_vencimento = form.data_primeiro_vencimento || form.data;
      } else {
        // Compra à vista — gera lançamento de saída no Financeiro
        body.forma_pagamento = form.forma_pagamento || 'pix';
      }
      const url = editingEntrada
        ? `${API_URL}/api/almoxarifado/entradas/${editingEntrada.id}`
        : `${API_URL}/api/almoxarifado/entradas`;
      const method = editingEntrada ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method, headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro ao registrar entrada'); }
      toast.success(editingEntrada ? 'Entrada atualizada' : 'Entrada registrada');
      setShowModal(false);
      setEditingEntrada(null);
      fetchEntradas();
      onChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const updateLinha = (idx, patch) => {
    const newLinhas = [...form.itens];
    newLinhas[idx] = { ...newLinhas[idx], ...patch };
    setForm({ ...form, itens: newLinhas });
  };

  const addLinha = () => setForm({ ...form, itens: [...form.itens, { item_id: '', quantidade: '', valor_unitario: '' }] });
  const removeLinha = (idx) => setForm({ ...form, itens: form.itens.filter((_, i) => i !== idx) });

  const totalForm = useMemo(() => {
    if (!form) return 0;
    return form.itens.reduce((acc, l) => {
      const q = Number(l.quantidade) || 0;
      const v = Number(l.valor_unitario) || 0;
      return acc + (q * v);
    }, 0);
  }, [form]);

  return (
    <div data-testid="almox-entradas-tab">
      <div className="flex flex-wrap gap-3 mb-4">
        <select value={filterCC} onChange={(e) => setFilterCC(e.target.value)}
                className="px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white">
          <option value="all">Todos destinos</option>
          {catalog.centros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select value={filterModal} onChange={(e) => setFilterModal(e.target.value)}
                className="px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white">
          <option value="all">Todas modalidades</option>
          <option value="a_vista">À Vista</option>
          <option value="a_prazo">A Prazo</option>
        </select>
        <div className="ml-auto">
          {canEdit && (
            <button data-testid="almox-entrada-nova"
                    onClick={openCreate}
                    className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg">
              <Plus size={18} /> Nova Entrada
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader className="animate-spin text-yellow-500" size={32} /></div>
      ) : entradas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ArrowDownToLine size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma entrada registrada.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="almox-entradas-lista">
          {entradas.map(e => (
            <div key={e.id} className="bg-[#1A1A1A] border border-gray-800 rounded-lg overflow-hidden">
              <div className="p-4 flex items-center justify-between flex-wrap gap-3 cursor-pointer hover:bg-[#1f1f1f]"
                   onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                   data-testid={`almox-entrada-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ArrowDownToLine size={16} className="text-green-400" />
                    <span className="text-white font-medium">{e.data}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded">{e.modalidade_pagamento_label}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded">{e.destino_label}</span>
                    {e.financeiro_lancamento_id && (
                      <span data-testid={`almox-entrada-financeiro-${e.id}`}
                            title="Lançamento de saída gerado no Financeiro"
                            className="text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded">
                        Financeiro ↗
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {e.fornecedor_nome || 'Sem fornecedor'} • {e.itens.length} item(ns) • {formatQty(e.quantidade_total)} un total
                    {e.numero_nota && <> • NF {e.numero_nota}</>}
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  <p className="text-white font-bold">{formatBRL(e.valor_total)}</p>
                  {canEdit && (
                    <>
                      <button onClick={(ev) => { ev.stopPropagation(); openEditEntrada(e); }}
                              data-testid={`almox-entrada-edit-${e.id}`}
                              title="Editar entrada"
                              className="p-1.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300">
                        <Edit2 size={14} />
                      </button>
                      {e.historico?.length > 0 && (
                        <button onClick={(ev) => { ev.stopPropagation(); setShowHistEntrada(e); }}
                                title={`${e.historico.length} alteração(ões)`}
                                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 relative">
                          <History size={14} />
                          <span className="absolute -top-1 -right-1 text-[9px] px-1 bg-yellow-500 text-black rounded-full">
                            {e.historico.length}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${expanded === e.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              {expanded === e.id && (
                <div className="border-t border-gray-800 p-4 space-y-2 bg-[#0A0A0A]">
                  {e.itens.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{it.item_nome} {it.item_codigo && <span className="text-gray-500">#{it.item_codigo}</span>}</span>
                      <span className="text-gray-400">
                        {formatQty(it.quantidade)} × {it.valor_unitario != null ? formatBRL(it.valor_unitario) : '-'}
                        {it.valor_total != null && <span className="text-white ml-2">= {formatBRL(it.valor_total)}</span>}
                      </span>
                    </div>
                  ))}
                  {e.observacoes && <p className="text-xs text-gray-500 mt-2 italic">{e.observacoes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal nova/editar entrada */}
      {showModal && form && (
        <Modal title={editingEntrada ? 'Editar Entrada' : 'Nova Entrada'} onClose={() => { setShowModal(false); setEditingEntrada(null); }} testid="almox-entrada-modal">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400">Data *</label>
                <input data-testid="almox-entrada-data" type="date" value={form.data}
                       onChange={(e) => setForm({ ...form, data: e.target.value })}
                       className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Modalidade de Pagamento *</label>
                <select data-testid="almox-entrada-modalidade" value={form.modalidade_pagamento}
                        onChange={(e) => setForm({ ...form, modalidade_pagamento: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  <option value="a_vista">À Vista</option>
                  <option value="a_prazo">A Prazo</option>
                </select>
              </div>
            </div>

            {/* Parcelamento (Bloco 7) — só aparece quando a_prazo */}
            {form.modalidade_pagamento === 'a_prazo' && (
              <ParcelamentoBlock form={form} setForm={setForm} />
            )}

            {/* Forma de pagamento (compra à vista) — gera lançamento de saída no Financeiro */}
            {form.modalidade_pagamento === 'a_vista' && (
              <div>
                <label className="text-xs text-gray-400">Forma de Pagamento (à vista)</label>
                <select data-testid="almox-entrada-forma-pagamento" value={form.forma_pagamento}
                        onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  <option value="pix">PIX</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="transferencia">Transferência</option>
                  <option value="cartao">Cartão</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Gera automaticamente um lançamento de saída no Financeiro com o valor total da compra.
                </p>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-400 flex items-center justify-between">
                <span>Fornecedor</span>
                <button type="button"
                        data-testid="almox-entrada-fornecedor-novo"
                        onClick={() => setShowSupplierModal(true)}
                        className="text-yellow-400 hover:text-yellow-300 text-xs flex items-center gap-1">
                  <UserPlus size={12} /> Cadastrar novo
                </button>
              </label>
              <div className="relative">
                {fornecedorSelecionado ? (
                  <div data-testid="almox-entrada-fornecedor-selecionado"
                       className="flex items-center justify-between px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg">
                    <div className="min-w-0">
                      <p className="text-white text-sm truncate">{fornecedorSelecionado.nome}</p>
                      <p className="text-gray-500 text-xs truncate">
                        {fornecedorSelecionado.cnpj ? `CNPJ ${formatCnpjDisplay(fornecedorSelecionado.cnpj)}` : 'sem CNPJ'}
                        {fornecedorSelecionado.telefone && ` • ${fornecedorSelecionado.telefone}`}
                      </p>
                    </div>
                    <button type="button" onClick={() => { setForm({ ...form, fornecedor_id: '', fornecedor_nome: '' }); setSupplierSearch(''); }}
                            className="p-1 text-gray-500 hover:text-white">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                    <input
                      data-testid="almox-entrada-fornecedor-search"
                      value={supplierSearch}
                      placeholder="Digite nome, CNPJ ou contato..."
                      onChange={(e) => { setSupplierSearch(e.target.value); setSupplierOpen(true); }}
                      onFocus={() => setSupplierOpen(true)}
                      onBlur={() => setTimeout(() => setSupplierOpen(false), 150)}
                      className="w-full pl-9 pr-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
                    {supplierOpen && fornecedoresFiltrados.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-[#111] border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto"
                           data-testid="almox-entrada-fornecedor-dropdown">
                        {fornecedoresFiltrados.map(f => (
                          <button type="button" key={f.id}
                                  data-testid={`almox-entrada-fornecedor-opt-${f.id}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setForm({ ...form, fornecedor_id: f.id, fornecedor_nome: '' });
                                    setSupplierSearch('');
                                    setSupplierOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-gray-800 border-b border-gray-800/50">
                            <p className="text-white text-sm flex items-center gap-2">
                              {f.nome}
                              {!f.dados_completos && (
                                <span className="text-[10px] px-1 py-0.5 bg-orange-500/10 text-orange-300 border border-orange-500/30 rounded">
                                  incompleto
                                </span>
                              )}
                            </p>
                            <p className="text-gray-500 text-xs">
                              {f.cnpj ? `CNPJ ${formatCnpjDisplay(f.cnpj)}` : 'sem CNPJ'}
                              {f.telefone && ` • ${f.telefone}`}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                    {supplierOpen && supplierSearch && fornecedoresFiltrados.length === 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-[#111] border border-gray-700 rounded-lg p-3 shadow-xl"
                           data-testid="almox-entrada-fornecedor-vazio">
                        <p className="text-gray-400 text-xs mb-2">Nenhum fornecedor encontrado.</p>
                        <button type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setShowSupplierModal(true)}
                                className="w-full px-3 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 rounded-lg text-sm flex items-center justify-center gap-2">
                          <UserPlus size={14} /> Cadastrar "{supplierSearch}"
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400">Destino *</label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setForm({ ...form, destino: 'geral', centro_de_custo_id: '' })}
                        data-testid="almox-entrada-destino-geral"
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.destino === 'geral' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
                  Estoque Geral
                </button>
                <button type="button" onClick={() => setForm({ ...form, destino: 'cc' })}
                        data-testid="almox-entrada-destino-cc"
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.destino === 'cc' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
                  Centro de Custo
                </button>
              </div>
              {form.destino === 'cc' && (
                <select value={form.centro_de_custo_id}
                        data-testid="almox-entrada-cc"
                        onChange={(e) => setForm({ ...form, centro_de_custo_id: e.target.value })}
                        className="w-full px-3 py-2 mt-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  <option value="">Selecione…</option>
                  {catalog.centros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              )}
            </div>

            <div>
              <label className="text-xs text-gray-400">Nº Nota Fiscal</label>
              <input value={form.numero_nota} onChange={(e) => setForm({ ...form, numero_nota: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>

            {/* Linhas de itens */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400">Itens *</label>
                <button type="button" onClick={addLinha} className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
                  <Plus size={12} /> Adicionar linha
                </button>
              </div>
              <div className="space-y-2">
                {form.itens.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`almox-entrada-linha-${idx}`}>
                    <select value={l.item_id} onChange={(e) => updateLinha(idx, { item_id: e.target.value })}
                            className="col-span-6 px-2 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
                      <option value="">Item…</option>
                      {items.map(it => (
                        <option key={it.id} value={it.id}>
                          {it.nome} ({formatQty(it.saldo_atual)} {it.unidade_medida_label})
                        </option>
                      ))}
                    </select>
                    <input type="number" min="0" step="any" placeholder="Qtd" value={l.quantidade}
                           onChange={(e) => updateLinha(idx, { quantidade: e.target.value })}
                           className="col-span-2 px-2 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
                    <input type="number" min="0" step="any" placeholder="R$ unit." value={l.valor_unitario}
                           onChange={(e) => updateLinha(idx, { valor_unitario: e.target.value })}
                           className="col-span-3 px-2 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
                    <button type="button" onClick={() => removeLinha(idx)}
                            className="col-span-1 p-2 text-red-400 hover:bg-red-500/10 rounded">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-right text-sm text-white mt-2">Total previsto: <strong>{formatBRL(totalForm)}</strong></p>
            </div>

            <div>
              <label className="text-xs text-gray-400">Observações</label>
              <textarea rows={2} value={form.observacoes}
                        onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg">Cancelar</button>
            <button data-testid="almox-entrada-save" disabled={saving} onClick={handleSave}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg disabled:opacity-50">
              {saving ? 'Salvando...' : 'Registrar Entrada'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal inline de cadastro rápido de fornecedor */}
      {showSupplierModal && (
        <Modal title="Cadastro Rápido de Fornecedor" onClose={() => setShowSupplierModal(false)} testid="almox-supplier-inline-modal">
          <SupplierForm
            compact
            defaultNome={supplierSearch}
            onClose={() => setShowSupplierModal(false)}
            onSaved={async (saved) => {
              setShowSupplierModal(false);
              await reloadFornecedores();
              if (saved?.id && form) {
                setForm({ ...form, fornecedor_id: saved.id, fornecedor_nome: '' });
                setSupplierSearch('');
              }
            }}
          />
        </Modal>
      )}

      {showHistEntrada && (
        <Modal title={`Histórico — ${showHistEntrada.data}`} onClose={() => setShowHistEntrada(null)} testid="almox-entrada-historico-modal">
          <HistoricoTimeline entries={showHistEntrada.historico || []} />
        </Modal>
      )}
    </div>
  );
}

// ============ SaidasTab ============
function SaidasTab({ catalog, canEdit, onChange }) {
  const [saidas, setSaidas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterNat, setFilterNat] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [editingSaida, setEditingSaida] = useState(null);
  const [showHistSaida, setShowHistSaida] = useState(null);

  const fetchSaidas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterNat !== 'all') params.append('natureza', filterNat);
      const r = await fetch(`${API_URL}/api/almoxarifado/saidas?${params}`, { headers: authHeaders() });
      if (r.ok) setSaidas(await r.json());
    } finally { setLoading(false); }
  }, [filterNat]);

  useEffect(() => { fetchSaidas(); }, [fetchSaidas]);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API_URL}/api/almoxarifado/itens?ativo=true`, { headers: authHeaders() });
      if (r.ok) setItems(await r.json());
    })();
  }, []);

  const openCreate = () => {
    setForm({
      data: todayISO(),
      natureza: 'n1_os_ativo',
      work_order_id: '',
      ativo_id: '',
      centro_de_custo_id: '',
      requisitante: '',
      observacoes: '',
      itens: [{ item_id: '', quantidade: '' }],
    });
    setEditingSaida(null);
    setShowModal(true);
  };

  const openEditSaida = (s) => {
    setEditingSaida(s);
    setForm({
      data: s.data,
      natureza: s.natureza,
      work_order_id: s.work_order_id || '',
      ativo_id: s.ativo_id || '',
      centro_de_custo_id: s.centro_de_custo_id || '',
      requisitante: s.requisitante || '',
      observacoes: s.observacoes || '',
      itens: s.itens.map(it => ({ item_id: it.item_id, quantidade: it.quantidade })),
    });
    setShowModal(true);
  };

  const itensFiltrados = useMemo(() => {
    if (!form) return items;
    if (form.natureza === 'n3_consumo_geral') return items.filter(it => it.consumo_geral);
    return items;
  }, [items, form]);

  const handleSave = async () => {
    if (!form.data) return toast.error('Data obrigatória');
    const linhas = form.itens.filter(l => l.item_id && Number(l.quantidade) > 0);
    if (linhas.length === 0) return toast.error('Adicione ao menos um item válido');

    if (form.natureza === 'n1_os_ativo' && !form.work_order_id && !form.ativo_id) {
      return toast.error('Natureza 1 exige selecionar OS ou ativo');
    }
    if (form.natureza === 'n2_setor' && !form.centro_de_custo_id) {
      return toast.error('Natureza 2 exige selecionar Centro de Custo');
    }

    setSaving(true);
    try {
      const body = {
        data: form.data,
        natureza: form.natureza,
        requisitante: form.requisitante || null,
        observacoes: form.observacoes || null,
        itens: linhas.map(l => ({ item_id: l.item_id, quantidade: Number(l.quantidade) })),
      };
      if (form.natureza === 'n1_os_ativo') {
        body.work_order_id = form.work_order_id || null;
        body.ativo_id = form.ativo_id || null;
      } else if (form.natureza === 'n2_setor') {
        body.centro_de_custo_id = form.centro_de_custo_id;
      }
      const url = editingSaida
        ? `${API_URL}/api/almoxarifado/saidas/${editingSaida.id}`
        : `${API_URL}/api/almoxarifado/saidas`;
      const method = editingSaida ? 'PUT' : 'POST';
      const r = await fetch(url, {
        method, headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro ao registrar saída'); }
      const saida = await r.json();
      if (saida.alertas_estoque_minimo?.length) {
        toast.warning(`Atenção: ${saida.alertas_estoque_minimo.join(', ')} ficou(aram) abaixo do mínimo`);
      } else {
        toast.success(editingSaida ? 'Saída atualizada' : 'Saída registrada');
      }
      setShowModal(false);
      setEditingSaida(null);
      fetchSaidas();
      onChange?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const updateLinha = (idx, patch) => {
    const novo = [...form.itens];
    novo[idx] = { ...novo[idx], ...patch };
    setForm({ ...form, itens: novo });
  };
  const addLinha = () => setForm({ ...form, itens: [...form.itens, { item_id: '', quantidade: '' }] });
  const removeLinha = (idx) => setForm({ ...form, itens: form.itens.filter((_, i) => i !== idx) });

  return (
    <div data-testid="almox-saidas-tab">
      <div className="flex flex-wrap gap-3 mb-4">
        <select data-testid="almox-saidas-filter-natureza" value={filterNat} onChange={(e) => setFilterNat(e.target.value)}
                className="px-3 py-2.5 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white">
          <option value="all">Todas naturezas</option>
          {catalog.naturezas.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
        </select>
        <div className="ml-auto">
          {canEdit && (
            <button data-testid="almox-saida-nova" onClick={openCreate}
                    className="flex items-center gap-2 px-4 py-2.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg">
              <Plus size={18} /> Nova Saída
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader className="animate-spin text-yellow-500" size={32} /></div>
      ) : saidas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ArrowUpFromLine size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma saída registrada.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="almox-saidas-lista">
          {saidas.map(s => (
            <div key={s.id} className="bg-[#1A1A1A] border border-gray-800 rounded-lg overflow-hidden">
              <div className="p-4 flex items-center justify-between flex-wrap gap-3 cursor-pointer hover:bg-[#1f1f1f]"
                   onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                   data-testid={`almox-saida-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ArrowUpFromLine size={16} className="text-red-400" />
                    <span className="text-white font-medium">{s.data}</span>
                    <span className={`text-xs px-2 py-0.5 rounded border ${NATUREZA_COLORS[s.natureza] || ''}`}>
                      {s.natureza_label}
                    </span>
                    {s.alertas_estoque_minimo?.length > 0 && (
                      <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-300 border border-orange-500/40 rounded inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> {s.alertas_estoque_minimo.length} alerta(s)
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 text-sm mt-1">
                    {s.work_order_numero && <>OS #{s.work_order_numero} • </>}
                    {s.ativo_nome && <>Ativo: {s.ativo_nome} • </>}
                    {s.centro_de_custo_nome && <>CC: {s.centro_de_custo_nome} • </>}
                    {s.itens.length} item(ns) • {formatQty(s.quantidade_total)} un total
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {canEdit && (
                    <>
                      <button onClick={(ev) => { ev.stopPropagation(); openEditSaida(s); }}
                              data-testid={`almox-saida-edit-${s.id}`}
                              title="Editar saída"
                              className="p-1.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300">
                        <Edit2 size={14} />
                      </button>
                      {s.historico?.length > 0 && (
                        <button onClick={(ev) => { ev.stopPropagation(); setShowHistSaida(s); }}
                                title={`${s.historico.length} alteração(ões)`}
                                className="p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 relative">
                          <History size={14} />
                          <span className="absolute -top-1 -right-1 text-[9px] px-1 bg-yellow-500 text-black rounded-full">
                            {s.historico.length}
                          </span>
                        </button>
                      )}
                    </>
                  )}
                  <ChevronDown size={16} className={`text-gray-500 transition-transform ${expanded === s.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              {expanded === s.id && (
                <div className="border-t border-gray-800 p-4 space-y-2 bg-[#0A0A0A]">
                  {s.itens.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-gray-300">{it.item_nome}</span>
                      <span className="text-gray-400">
                        <span className="text-red-400">-{formatQty(it.quantidade)}</span>
                        {it.saldo_depois != null && (
                          <span className={`ml-2 ${it.saldo_depois <= 0 ? 'text-red-400' : 'text-gray-500'}`}>
                            (saldo: {formatQty(it.saldo_depois)})
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                  {s.requisitante && <p className="text-xs text-gray-500 mt-2">Requisitante: {s.requisitante}</p>}
                  {s.observacoes && <p className="text-xs text-gray-500 mt-1 italic">{s.observacoes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal nova/editar saída */}
      {showModal && form && (
        <Modal title={editingSaida ? 'Editar Saída' : 'Nova Saída'} onClose={() => { setShowModal(false); setEditingSaida(null); }} testid="almox-saida-modal">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400">Natureza da Saída *</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {catalog.naturezas.map(n => (
                  <button key={n.value} type="button"
                          data-testid={`almox-saida-nat-${n.value}`}
                          onClick={() => setForm({ ...form, natureza: n.value, work_order_id: '', ativo_id: '', centro_de_custo_id: '' })}
                          className={`px-2 py-2 rounded-lg border text-xs transition-colors ${form.natureza === n.value ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
                    {n.label.replace(/Natureza \d+ — /, '')}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400">Data *</label>
              <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>

            {/* Campos condicionais por natureza */}
            {form.natureza === 'n1_os_ativo' && (
              <div className="space-y-3 p-3 bg-blue-500/5 border border-blue-500/30 rounded-lg">
                <p className="text-xs text-blue-300">Selecione OS <strong>ou</strong> ativo (o CC será derivado automaticamente).</p>
                <div>
                  <label className="text-xs text-gray-400">Ordem de Serviço (aberta)</label>
                  <select data-testid="almox-saida-os" value={form.work_order_id}
                          onChange={(e) => setForm({ ...form, work_order_id: e.target.value })}
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                    <option value="">— Sem OS —</option>
                    {catalog.workOrders.map(w => (
                      <option key={w.id} value={w.id}>OS #{w.order_number} — {w.os_type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Equipamento / Veículo</label>
                  <select data-testid="almox-saida-ativo" value={form.ativo_id}
                          onChange={(e) => setForm({ ...form, ativo_id: e.target.value })}
                          className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                    <option value="">— Sem ativo —</option>
                    {catalog.machines.map(m => (
                      <option key={m.id} value={m.id}>
                        {[m.brand, m.model, m.serial_number ? `(${m.serial_number})` : ''].filter(Boolean).join(' ') || m.type || 'Máquina'}
                      </option>
                    ))}
                  </select>
                  {form.ativo_id && (
                    <p className="mt-1 text-xs text-blue-300" data-testid="almox-saida-cc-derivado">
                      O custo será atribuído ao equipamento selecionado (centro de custo).
                    </p>
                  )}
                </div>
              </div>
            )}
            {form.natureza === 'n2_setor' && (
              <div className="p-3 bg-purple-500/5 border border-purple-500/30 rounded-lg">
                <label className="text-xs text-purple-300">Centro de Custo *</label>
                <select data-testid="almox-saida-cc" value={form.centro_de_custo_id}
                        onChange={(e) => setForm({ ...form, centro_de_custo_id: e.target.value })}
                        className="w-full px-3 py-2 mt-1 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white">
                  <option value="">Selecione o CC...</option>
                  {catalog.centros.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}
            {form.natureza === 'n3_consumo_geral' && (
              <div className="p-3 bg-emerald-500/5 border border-emerald-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-emerald-300 mt-0.5 shrink-0" />
                <p className="text-xs text-emerald-200">
                  Consumo geral — sem vínculo a CC, OS ou ativo. Apenas itens marcados como <strong>"Consumo Geral"</strong> podem ser selecionados.
                </p>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-400">Requisitante</label>
              <input value={form.requisitante} onChange={(e) => setForm({ ...form, requisitante: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400">Itens *</label>
                <button type="button" onClick={addLinha} className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1">
                  <Plus size={12} /> Adicionar linha
                </button>
              </div>
              <div className="space-y-2">
                {form.itens.map((l, idx) => {
                  const item = items.find(i => i.id === l.item_id);
                  const saldoApos = item ? item.saldo_atual - (Number(l.quantidade) || 0) : null;
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`almox-saida-linha-${idx}`}>
                      <select value={l.item_id} onChange={(e) => updateLinha(idx, { item_id: e.target.value })}
                              className="col-span-7 px-2 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
                        <option value="">Item…</option>
                        {itensFiltrados.map(it => (
                          <option key={it.id} value={it.id}>
                            {it.nome} ({formatQty(it.saldo_atual)} {it.unidade_medida_label})
                          </option>
                        ))}
                      </select>
                      <input type="number" min="0" step="any" placeholder="Qtd" value={l.quantidade}
                             onChange={(e) => updateLinha(idx, { quantidade: e.target.value })}
                             className="col-span-3 px-2 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
                      <div className="col-span-1 text-xs flex items-center">
                        {item && saldoApos != null && (
                          <span className={saldoApos <= (item.estoque_minimo || 0) ? 'text-red-400' : 'text-gray-500'} title="Saldo após esta saída">
                            {formatQty(saldoApos)}
                          </span>
                        )}
                      </div>
                      <button type="button" onClick={() => removeLinha(idx)}
                              className="col-span-1 p-2 text-red-400 hover:bg-red-500/10 rounded">
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-400">Observações</label>
              <textarea rows={2} value={form.observacoes}
                        onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
            </div>
          </div>
          <div className="flex gap-2 mt-4 justify-end">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg">Cancelar</button>
            <button data-testid="almox-saida-save" disabled={saving} onClick={handleSave}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg disabled:opacity-50">
              {saving ? 'Salvando...' : (editingSaida ? 'Atualizar Saída' : 'Registrar Saída')}
            </button>
          </div>
        </Modal>
      )}

      {showHistSaida && (
        <Modal title={`Histórico — ${showHistSaida.data}`} onClose={() => setShowHistSaida(null)} testid="almox-saida-historico-modal">
          <HistoricoTimeline entries={showHistSaida.historico || []} />
        </Modal>
      )}
    </div>
  );
}

// ============ Dashboard tab ============
function DashboardTab({ refreshKey, onJumpTab }) {
  const [data, setData] = useState(null);
  const [alertas, setAlertas] = useState({ total: 0, items: [] });
  const [giro, setGiro] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [saldoR, alertasR, entradasR, saidasR, giroR] = await Promise.all([
          fetch(`${API_URL}/api/almoxarifado/saldo`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/almoxarifado/alertas-estoque-minimo`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/almoxarifado/entradas?data_inicio=${firstDayOfMonth()}&data_fim=${todayISO()}`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/almoxarifado/saidas?data_inicio=${firstDayOfMonth()}&data_fim=${todayISO()}`, { headers: authHeaders() }),
          fetch(`${API_URL}/api/almoxarifado/giro-estoque?limit=5`, { headers: authHeaders() }),
        ]);
        const saldo = saldoR.ok ? await saldoR.json() : [];
        const al = alertasR.ok ? await alertasR.json() : { items: [], total: 0 };
        const ent = entradasR.ok ? await entradasR.json() : [];
        const sai = saidasR.ok ? await saidasR.json() : [];
        const g = giroR.ok ? await giroR.json() : { items: [] };
        setAlertas(al);
        setGiro(g);
        setData({
          totalItens: saldo.length,
          abaixoMinimo: saldo.filter(s => s.abaixo_minimo).length,
          valorEntradas: ent.reduce((a, e) => a + (e.valor_total || 0), 0),
          totalEntradas: ent.length,
          totalSaidas: sai.length,
          qtdSaidas: sai.reduce((a, s) => a + (s.quantidade_total || 0), 0),
        });
      } finally { setLoading(false); }
    })();
  }, [refreshKey]);

  if (loading || !data) return <div className="flex items-center justify-center py-12"><Loader className="animate-spin text-yellow-500" size={32} /></div>;

  const maxGiro = giro.items.reduce((m, it) => Math.max(m, it.quantidade_total || 0), 0) || 1;

  return (
    <div data-testid="almox-dashboard-tab" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card label="Itens cadastrados" value={data.totalItens} icon={Package} color="bg-blue-500" onClick={() => onJumpTab('itens')} testid="card-total-itens" />
        <Card label="Abaixo do mínimo" value={data.abaixoMinimo} icon={AlertTriangle} color="bg-red-500" highlight={data.abaixoMinimo > 0} onClick={() => onJumpTab('itens')} testid="card-abaixo-minimo" />
        <Card label="Entradas no mês" value={formatBRL(data.valorEntradas)} sub={`${data.totalEntradas} pedido(s)`} icon={ArrowDownToLine} color="bg-green-500" onClick={() => onJumpTab('entradas')} testid="card-entradas" />
        <Card label="Saídas no mês" value={data.totalSaidas} sub={`${formatQty(data.qtdSaidas)} un total`} icon={ArrowUpFromLine} color="bg-purple-500" onClick={() => onJumpTab('saidas')} testid="card-saidas" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-4" data-testid="almox-painel-alertas">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-red-400" size={20} />
            <h3 className="text-white font-semibold">Alertas de Estoque Mínimo</h3>
            {alertas.total > 0 && <span className="ml-auto text-xs px-2 py-0.5 bg-red-500/20 text-red-300 border border-red-500/40 rounded">{alertas.total}</span>}
          </div>
          {alertas.total === 0 ? (
            <p className="text-gray-500 text-sm flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-400" /> Nenhum item abaixo do mínimo.
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {alertas.items.map(a => (
                <div key={a.item_id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg border border-gray-800">
                  <div>
                    <p className="text-white text-sm">{a.nome}</p>
                    <p className="text-xs text-gray-500">Saldo: {formatQty(a.saldo_atual)} • Mínimo: {formatQty(a.estoque_minimo)}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded border ${a.severidade === 'critico' ? 'bg-red-500/20 text-red-300 border-red-500/40' : 'bg-orange-500/20 text-orange-300 border-orange-500/40'}`}>
                    {a.severidade === 'critico' ? 'CRÍTICO' : 'ATENÇÃO'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#1A1A1A] border border-gray-800 rounded-lg p-4" data-testid="almox-painel-giro">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="text-yellow-400" size={20} />
            <h3 className="text-white font-semibold">Top 5 — Giro de Estoque (mês)</h3>
          </div>
          {giro.items.length === 0 ? (
            <p className="text-gray-500 text-sm">Sem saídas registradas no período.</p>
          ) : (
            <div className="space-y-2">
              {giro.items.map((it, i) => {
                const pct = ((it.quantidade_total / maxGiro) * 100).toFixed(0);
                return (
                  <div key={it.item_id || i} data-testid={`almox-giro-item-${i}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-white truncate flex-1 mr-2">
                        <span className="text-gray-500 mr-1">#{i + 1}</span>{it.nome}
                      </span>
                      <span className="text-yellow-300 font-mono text-xs">
                        {formatQty(it.quantidade_total)} {it.unidade_medida_label}
                      </span>
                    </div>
                    <div className="h-2 bg-[#0A0A0A] rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded transition-all"
                           style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{it.movimentacoes} movimentação(ões)</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, sub, icon: Icon, color, highlight, onClick, testid }) {
  return (
    <button onClick={onClick}
            data-testid={testid}
            className={`text-left bg-[#1A1A1A] border ${highlight ? 'border-red-500/40' : 'border-gray-800'} rounded-lg p-4 hover:border-yellow-500/40 transition-colors`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
        <div className={`p-1.5 rounded ${color} bg-opacity-20`}><Icon size={16} className="text-white" /></div>
      </div>
      <p className={`text-2xl font-bold ${highlight ? 'text-red-400' : 'text-white'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </button>
  );
}

// ============ Página principal ============
export default function Almoxarifado() {
  const { user } = useAuth();
  const catalog = useCatalog();
  const [tab, setTab] = useState('dashboard');
  const [refreshKey, setRefreshKey] = useState(0);

  const canEdit = !!(
    user?.is_master ||
    user?.role === 'admin' ||
    user?.editar_almoxarifado ||
    user?.permissions?.editar_almoxarifado
  );

  const tabs = [
    { id: 'dashboard', label: 'Painel', icon: LayoutDashboard },
    { id: 'itens', label: 'Itens', icon: Boxes },
    { id: 'entradas', label: 'Entradas', icon: ArrowDownToLine },
    { id: 'saidas', label: 'Saídas', icon: ArrowUpFromLine },
  ];

  const bump = () => setRefreshKey(k => k + 1);

  return (
    <div className="space-y-6" data-testid="almoxarifado-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white">Almoxarifado</h1>
        <p className="text-gray-400 mt-1">Controle de itens, entradas e saídas do estoque</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`almox-tab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                    tab === t.id ? 'border-yellow-500 text-yellow-400' : 'border-transparent text-gray-400 hover:text-white'
                  }`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardTab refreshKey={refreshKey} onJumpTab={setTab} />}
      {tab === 'itens' && <ItensTab catalog={catalog} canEdit={canEdit} onChange={bump} />}
      {tab === 'entradas' && <EntradasTab catalog={catalog} canEdit={canEdit} onChange={bump} />}
      {tab === 'saidas' && <SaidasTab catalog={catalog} canEdit={canEdit} onChange={bump} />}
    </div>
  );
}


// ============ Bloco 7: Parcelamento e Histórico ============

function ParcelamentoBlock({ form, setForm }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const valorTotal = useMemo(() => {
    return (form.itens || []).reduce((a, l) => {
      const q = Number(l.quantidade) || 0;
      const v = Number(l.valor_unitario) || 0;
      return a + (q * v);
    }, 0);
  }, [form.itens]);

  const dataVenc = form.data_primeiro_vencimento || form.data;
  const n = Number(form.numero_parcelas) || 1;

  useEffect(() => {
    let cancelled = false;
    if (valorTotal <= 0 || n < 1 || !dataVenc) {
      setPreview(null);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({
      valor_total: String(valorTotal),
      numero_parcelas: String(n),
      data_primeiro_vencimento: dataVenc,
    });
    fetch(`${API_URL}/api/contas-pagar/preview-parcelas?${params}`, {
      method: 'POST', headers: authHeaders(),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setPreview(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [valorTotal, n, dataVenc]);

  return (
    <div className="p-3 bg-yellow-500/5 border border-yellow-500/30 rounded-lg space-y-3"
         data-testid="almox-entrada-parcelamento">
      <div className="flex items-center gap-2 text-yellow-300 text-sm font-medium">
        <CreditCardIcon /> Parcelamento (Contas a Pagar)
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400">Número de parcelas *</label>
          <input data-testid="almox-entrada-numero-parcelas" type="number" min="1" max="120"
                 value={form.numero_parcelas}
                 onChange={(e) => setForm({ ...form, numero_parcelas: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
        </div>
        <div>
          <label className="text-xs text-gray-400">1º vencimento *</label>
          <input data-testid="almox-entrada-primeiro-venc" type="date"
                 value={form.data_primeiro_vencimento}
                 onChange={(e) => setForm({ ...form, data_primeiro_vencimento: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white" />
        </div>
      </div>
      {/* Resumo das parcelas */}
      {loading && <p className="text-xs text-gray-500">Calculando…</p>}
      {!loading && preview?.parcelas?.length > 0 && (
        <div data-testid="almox-entrada-preview-parcelas" className="space-y-1">
          <p className="text-xs text-gray-400 mb-1">Resumo:</p>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {preview.parcelas.map((p) => (
              <div key={p.parcela_numero}
                   className="flex justify-between items-center text-xs px-2 py-1 bg-[#0A0A0A] rounded border border-gray-800">
                <span className="text-gray-300">
                  Parc. <strong className="text-yellow-300">{p.parcela_numero}/{p.parcela_total}</strong> — venc. {p.data_vencimento}
                </span>
                <span className="text-white font-mono">{formatBRL(p.valor)}</span>
              </div>
            ))}
          </div>
          <p className="text-right text-xs text-gray-400">
            Soma: <span className="text-yellow-300 font-mono">{formatBRL(preview.soma_validacao)}</span>
          </p>
        </div>
      )}
      {!loading && valorTotal <= 0 && (
        <p className="text-xs text-gray-500 italic">Preencha valor unitário dos itens para gerar parcelas.</p>
      )}
    </div>
  );
}

function CreditCardIcon() {
  return <span aria-hidden>💳</span>;
}

function FiscalSection({ form, setForm }) {
  const hasAny = !!(form.marca || form.nf_numero || form.nf_serie || form.nf_chave_acesso || form.nf_data_emissao);
  const [open, setOpen] = useState(hasAny);
  return (
    <div className="border border-gray-800 rounded-lg" data-testid="almox-item-fiscal-section">
      <button type="button" onClick={() => setOpen(!open)}
              data-testid="almox-item-fiscal-toggle"
              className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-300 hover:text-white">
        <span className="flex items-center gap-2">
          <FileText size={14} className="text-yellow-400" />
          Informações Fiscais (opcional)
          {hasAny && <span className="text-[10px] text-yellow-300 ml-1">preenchidas</span>}
        </span>
        <ChevronDown size={14} className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="p-3 pt-0 space-y-2 border-t border-gray-800">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-400">Marca</label>
              <input data-testid="almox-item-marca" value={form.marca}
                     onChange={(e) => setForm({ ...form, marca: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Data de Emissão NF</label>
              <input type="date" value={form.nf_data_emissao}
                     onChange={(e) => setForm({ ...form, nf_data_emissao: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Nº NF</label>
              <input data-testid="almox-item-nf-numero" value={form.nf_numero}
                     onChange={(e) => setForm({ ...form, nf_numero: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Série</label>
              <input value={form.nf_serie}
                     onChange={(e) => setForm({ ...form, nf_serie: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-400">Chave de Acesso (44 dígitos)</label>
              <input data-testid="almox-item-nf-chave" value={form.nf_chave_acesso}
                     placeholder="0000 0000 0000 0000 0000 0000 0000 0000 0000 0000 0000"
                     onChange={(e) => setForm({ ...form, nf_chave_acesso: e.target.value })}
                     className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm font-mono" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoricoTimeline({ entries }) {  if (!entries || entries.length === 0) {
    return <p className="text-gray-500 text-sm">Sem histórico de alterações.</p>;
  }
  return (
    <div className="space-y-3">
      {entries.map((h, i) => (
        <div key={i} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3" data-testid={`hist-entry-${i}`}>
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-yellow-300">{h.user || '—'}</span>
            <span className="text-gray-500">{(h.data || '').replace('T', ' ').slice(0, 19)}</span>
          </div>
          <div className="space-y-1 text-sm">
            {Object.entries(h.alteracoes || {}).map(([campo, v]) => (
              <div key={campo} className="text-gray-300">
                <span className="text-gray-500 text-xs uppercase mr-1">{campo}:</span>
                {Array.isArray(v) ? (
                  <span className="text-xs">{v.length} item(ns)</span>
                ) : v && typeof v === 'object' && 'de' in v ? (
                  <>
                    <span className="text-red-400 line-through mr-1">{String(v.de ?? '∅')}</span>
                    <span className="text-emerald-400">→ {String(v.para ?? '∅')}</span>
                  </>
                ) : (
                  <span className="text-xs">{JSON.stringify(v)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
